/**
 * UTC date/range label formatters for the uptime bars and bucket modals.
 * Pure, dependency-free (beyond `formatPct`) — extracted from `App.tsx` so the
 * modal title, hover label, foot-edge label and tooltip range share one
 * formatter set.
 */
import { formatPct } from './status-mapping';
import type { UptimeWindow, WindowBucket } from './status-types';

export function formatBucketModalTitle(
  b: WindowBucket,
  w: UptimeWindow,
): string {
  if (!b.startAt) return 'No data';
  const start = new Date(b.startAt);
  const dateStr = start.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  if (w === '90d') return dateStr;
  const hm = (d: Date) =>
    d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  if (w === '24h') {
    return `${dateStr} · ${hm(start)} — ${hm(new Date(b.endAt))} UTC`;
  }
  return `${dateStr} · ${hm(start)} UTC`;
}

/** `Mon D, HH:mm` (UTC) — foot label for an anchored window's edges. */
export function formatWindowEdge(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  return `${date}, ${time}`;
}

export function formatBucketHoverLabel(
  b: WindowBucket,
  w: UptimeWindow,
): string | null {
  if (!b.startAt) return null;
  const start = new Date(b.startAt);
  const dateStr = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  const time =
    w === '90d'
      ? start.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : `${dateStr}, ${start.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        })} UTC`;
  if (b.totalProbes === 0) return `${time} · no data`;
  const pct = formatPct(b.successPct);
  // The failure mark on a green cell (DESIGN.md §6.0) is `aria-hidden`, so the
  // failures have to reach non-visual users through this label — it feeds both
  // the cell's `title` and its `aria-label`. Without this clause a day that
  // renders green-with-a-stripe would be indistinguishable from a clean day to
  // a screen reader, which is exactly what §14 forbids ("colour is never the
  // only encoding").
  if (b.downCount > 0) {
    return `${time} · ${pct} · ${String(b.downCount)} of ${String(b.totalProbes)} failed`;
  }
  return `${time} · ${pct}`;
}

export function formatBucketTimeRange(b: WindowBucket, w: UptimeWindow): string {
  if (!b.startAt) return '—';
  const start = new Date(b.startAt);
  if (w === '90d') {
    return start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  const hm = (d: Date) =>
    d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  if (w === '24h') {
    const end = new Date(b.endAt);
    return `${hm(start)} — ${hm(end)} UTC`;
  }
  return `${hm(start)} UTC`;
}
